import React from "react";
import { NoxusInput } from "./InputField";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { SpotSelect } from "./SelectField";
import { Control, Controller } from "react-hook-form";
import { CSS } from "@dnd-kit/utilities";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  useSortable,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { GripVertical, Plus, Trash } from "lucide-react";
import { NoxusFormLabel } from "./NoxusFormLabel";
import { NoxusIconButton } from "../UI/NoxusIconButton";
import { Button } from "../shadcn/button";
import { findMaxStringValue } from "@/utils/generic";
import { cn } from "@/lib/utils";
import { DataTypeIcon, hasTypeIcon } from "@/icons/DataTypeIcon";

function SortableItem({
  id,
  children,
  isDisabled,
}: {
  id: string;
  children: React.ReactNode;
  isDisabled?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id, disabled: isDisabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} className="w-full bg-white py-1">
      <div className="flex items-end">
        <div className="flex-1">{children}</div>
        <div
          {...attributes}
          {...listeners}
          className={cn(
            "p-3 -mb-0.5 cursor-grab text-primary",
            isDisabled && "cursor-not-allowed opacity-50"
          )}
        >
          <GripVertical size="1rem" fill="060C11" strokeWidth="1.25px" />
        </div>
      </div>
    </div>
  );
}

interface ITypeDictValue {
  label: string;
  value: string;
}

interface ISelectArrayDictInputProps {
  isDisabled?: boolean;
  label: string;
  tooltip?: string;
  helperText?: string;
  value: ITypeDictValue[];
  onChange: (value: ITypeDictValue[]) => boolean;
  name?: string;
  name_label: string;
  name_prefix: string;
  value_label: string;
  values: string[];
  default_value: string;
  /**
   * When provided, the left field renders as a Select populated from these
   * values instead of a free-text input. Use when each row should reference
   * an existing item (e.g. a manually-added node input) rather than define
   * a new one. Mirrors the existing `values` prop on the right field.
   */
  name_values?: string[];
  /**
   * When true, each row's right dropdown filters out values already picked
   * in other rows so the same option can't be selected twice. The currently
   * selected value remains in its own row so it still renders.
   */
  unique_values?: boolean;
  /**
   * Minimum number of entries the user must keep. Defaults to 1 to
   * preserve legacy behavior where the field always has at least one
   * row. Set to 0 for genuinely-optional arrays.
   */
  min_items?: number;
  addLabel?: string;
  // With name_values: locks each row's type to the one implied by its name.
  name_types?: Record<string, string>;
}

const SelectArrayDictInput = ({
  isDisabled,
  label,
  tooltip,
  helperText,
  value: rawValue,
  onChange,
  name,
  name_label,
  name_prefix,
  value_label,
  values,
  default_value,
  name_values,
  unique_values = false,
  min_items = 1,
  addLabel = "Add entry",
  name_types,
}: ISelectArrayDictInputProps) => {
  const value = rawValue ?? [];
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const [inputs, setInputs] = useState<string[]>(
    value?.map((v) => {
      return v.label;
    }) || []
  );

  const committedLabels = JSON.stringify(value.map((v) => v.label));
  useEffect(() => {
    setInputs(value.map((v) => v.label));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [committedLabels]);

  const typesOptions = useMemo(() => {
    // Show a data-type icon when the option is a recognized type (e.g. the Run
    // Code variable types); a no-op for non-type select arrays. "List of X"
    // labels render X's icon stacked, matching the chip type selectors.
    return values.map((v) => {
      const listMatch = /^List of (.+)$/.exec(v);
      const elementLabel = listMatch ? listMatch[1] : v;
      return {
        value: v,
        label: v,
        icon: hasTypeIcon(elementLabel) ? (
          <DataTypeIcon
            dataType={elementLabel}
            isList={!!listMatch}
            size={14}
          />
        ) : undefined,
      };
    });
  }, [values]);

  const nameOptions = useMemo(() => {
    return (name_values ?? []).map((v) => ({ value: v, label: v }));
  }, [name_values]);

  const addEntry = () => {
    let name: string;
    if (name_values) {
      // Reference mode: pick the first name_value that isn't already used on
      // another row, so the new row arrives with a real, selectable label.
      // (The Add button is disabled when none are available — see below.)
      const used = new Set(value.map((v) => v.label));
      name = name_values.find((nv) => !used.has(nv)) ?? "";
    } else {
      const maxOutputValue = findMaxStringValue(
        value.map((i) => i.label),
        new RegExp(`^${name_prefix}(\\d+)$`)
      );
      name = `${name_prefix}${maxOutputValue + 1}`;
    }
    const entryType = name_types?.[name] ?? default_value;
    if (onChange([...value, { label: name, value: entryType }])) {
      setInputs([...inputs, name]);
    } else {
      setInputs([...inputs]);
    }
  };

  // Disable Add Entry when there's nothing left to pick:
  //  - name_values mode: every name_value is already in use
  //  - unique_values mode: every right-side option is already in use
  const addEntryDisabled =
    (name_values !== undefined && value.length >= name_values.length) ||
    (unique_values && value.length >= values.length);

  const handleDeleteClick = (index: number) => {
    const newInputs = [...inputs];
    newInputs.splice(index, 1);
    setInputs(newInputs);
    if (onChange(value.filter((v, i) => i !== index))) {
      setInputs(newInputs);
    } else {
      setInputs([...inputs]);
    }
  };

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over) return;

    if (active.id !== over.id) {
      const new_value = arrayMove(
        value,
        value.findIndex((v) => v.label === active.id),
        value.findIndex((v) => v.label === over.id)
      );

      const new_inputs = arrayMove(
        inputs,
        inputs.findIndex((v) => v === active.id),
        inputs.findIndex((v) => v === over.id)
      );
      onChange(new_value);
      setInputs(new_inputs);
    }
  }

  const onInputChange = (
    newValue: ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const newInputs = [...inputs];
    newInputs[index] = newValue.currentTarget.value;
    setInputs(newInputs);
  };

  const updateValueOnBlur = (
    event: React.FocusEvent<any, Element>,
    index: number
  ) => {
    const oldINputs = structuredClone(inputs);
    const changeSuccess = onChange(
      value.map((v, i) => {
        if (i === index) {
          const _value = inputs[i].trim() || v.label;

          setInputs((inputs) => {
            return inputs.map((input, i) => {
              if (i === index) {
                return _value;
              }
              return input;
            });
          });
          return { ...v, label: _value };
        }
        return v;
      })
    );

    if (!changeSuccess) {
      setInputs(oldINputs);
    }
  };

  return (
    <div>
      <NoxusFormLabel label={label} tooltip={tooltip} name={name} />
      {helperText && (
        <p className="mt-1 text-xs text-muted-foreground">{helperText}</p>
      )}
      <div className="w-full mt-4 flex flex-col gap-3">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={value.map((v) => v.label)}
            strategy={verticalListSortingStrategy}
          >
            {value.map(({ label, value: type }, index) => {
              return (
                <SortableItem key={label} id={label} isDisabled={isDisabled}>
                  <div key={index} className="w-full flex gap-4 items-center">
                    <div
                      className="flex-1 w-1/2"
                      role="group"
                      aria-label={`${name_label} row ${index + 1}`}
                    >
                      {name_values ? (
                        <SpotSelect
                          options={nameOptions.filter(
                            (o) =>
                              o.value === label ||
                              !value.some((v) => v.label === o.value)
                          )}
                          value={nameOptions.find(
                            (o) => o.value === inputs[index]
                          )}
                          onChange={({ value: changeValue }) => {
                            const accepted = onChange(
                              value.map((v, i) =>
                                i === index
                                  ? {
                                      ...v,
                                      label: changeValue,
                                      value:
                                        name_types?.[changeValue] ?? v.value,
                                    }
                                  : v
                              )
                            );
                            if (accepted) {
                              setInputs(
                                inputs.map((s, i) =>
                                  i === index ? changeValue : s
                                )
                              );
                            }
                          }}
                          label={name_label}
                          isDisabled={isDisabled}
                        />
                      ) : (
                        <NoxusInput
                          value={inputs[index]}
                          onChange={(e) => onInputChange(e, index)}
                          onBlur={(e) => updateValueOnBlur(e, index)}
                          label={name_label}
                          disable={isDisabled}
                        />
                      )}
                    </div>
                    <div className="flex-1 w-1/2">
                      <SpotSelect
                        options={
                          unique_values
                            ? typesOptions.filter(
                                (o) =>
                                  o.value === type ||
                                  !value.some(
                                    (v, i) => i !== index && v.value === o.value
                                  )
                              )
                            : typesOptions
                        }
                        value={typesOptions.find((o) => o.value === type)}
                        onChange={({ value: changeValue }) => {
                          onChange(
                            value.map((v, i) => {
                              if (i === index) {
                                return {
                                  ...v,
                                  value: changeValue,
                                };
                              }
                              return v;
                            })
                          );
                        }}
                        label={value_label}
                        isDisabled={isDisabled || name_types !== undefined}
                      />
                    </div>

                    <div className="self-end mb-1">
                      <NoxusIconButton
                        aria-label="Delete"
                        icon={Trash}
                        variant="smallSecondary"
                        onClick={() => handleDeleteClick(index)}
                        isDisabled={isDisabled || value.length <= min_items}
                        disabledTooltip={
                          value.length <= min_items
                            ? min_items === 1
                              ? "Can't have less than 1 value"
                              : `Can't have less than ${min_items} value${min_items === 1 ? "" : "s"}`
                            : undefined
                        }
                      />
                    </div>
                  </div>
                </SortableItem>
              );
            })}
          </SortableContext>
        </DndContext>
      </div>
      <Button
        onClick={addEntry}
        className="mt-2 w-full"
        variant="outline"
        isDisabled={isDisabled || addEntryDisabled}
        title={
          addEntryDisabled
            ? "All available options are already used"
            : undefined
        }
      >
        {addLabel}
        <Plus size="1.25rem" strokeWidth="1.25px" />
      </Button>
    </div>
  );
};

interface ISelectArrayDictFieldProps {
  name: string;
  label: string;
  tooltip?: string;
  helperText?: string;
  defaultValue?: { label: string; type: string }[];
  control: Control<any>;
  onChange?: (value: ITypeDictValue[]) => void;
  isDisabled?: boolean;
  name_label: string;
  name_prefix: string;
  value_label: string;
  values: string[];
  default_value: string;
  name_values?: string[];
  unique_values?: boolean;
  min_items?: number;
  addLabel?: string;
  name_types?: Record<string, string>;
}

export const SelectArrayDictField = ({
  name,
  label,
  tooltip,
  helperText,
  control,
  defaultValue,
  onChange,
  isDisabled,
  name_label,
  name_prefix,
  value_label,
  values,
  default_value,
  name_values,
  unique_values,
  min_items,
  addLabel,
  name_types,
}: ISelectArrayDictFieldProps) => {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      disabled={isDisabled}
      render={({ field }) => {
        const handleOnChange = (value: ITypeDictValue[]) => {
          const parsedLabels = new Set<string>();
          const hasRepeatedNames = value
            .map((v) => v.label)
            .some((label) => {
              if (parsedLabels.has(label)) {
                return true;
              }
              parsedLabels.add(label);
              return false;
            });

          if (!hasRepeatedNames) {
            field.onChange(value);
            if (onChange) onChange(value);
            return true;
          } else {
            // field.value has the old value, meaning it won't be updated
            if (onChange) onChange(field.value);
            return false;
          }
        };
        return (
          <SelectArrayDictInput
            name={name}
            // defaultValue={defaultValue}
            label={label}
            tooltip={tooltip}
            helperText={helperText}
            value={field.value}
            onChange={handleOnChange}
            name_label={name_label}
            name_prefix={name_prefix}
            value_label={value_label}
            values={values}
            default_value={default_value}
            name_values={name_values}
            unique_values={unique_values}
            isDisabled={isDisabled}
            min_items={min_items}
            addLabel={addLabel}
            name_types={name_types}
          />
        );
      }}
    />
  );
};
